import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import NotificationDropdown from './NotificationDropdown';
import EntitySwitcher from './EntitySwitcher';
import Breadcrumbs from './Breadcrumbs';
import CommandPalette from './CommandPalette';
import KeyboardShortcutsDialog from './KeyboardShortcutsDialog';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { getEntity, getEntityLogo, getUserPreferences, updateUserPreferences, getProfilePhoto } from '../../lib/api';
import { toast } from 'sonner';
import {  
  LayoutDashboard, 
  FileText, 
  LogOut,
  Building2,
  FileSpreadsheet,
  ClipboardCheck,
  ChevronRight,
  ChevronDown,
  Users,
  History,
  Settings,
  Search,
  BarChart3,
  HelpCircle,
  TrendingUp,
  User,
  Receipt,
  DollarSign,
  Database,
  BookOpen,
  Shield,
  Calendar,
  Cloud,
  Zap,
  Menu,
  X,
  Upload,
  KeyRound
} from 'lucide-react';

const defaultNavItems = [
  { id: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { 
    id: 'accounts', 
    path: '/accounts', 
    label: 'Accounts', 
    icon: Building2,
    children: [
      { id: 'chart-of-accounts', path: '/accounts', label: 'Chart of Accounts', icon: BookOpen, exact: true },
      { id: 'general-ledger', path: '/accounts/general-ledger', label: 'General Ledger', icon: Database },
      { id: 'trial-balance', path: '/accounts/trial-balance', label: 'Trial Balance', icon: Receipt },
      { id: 'data-import', path: '/data-import', label: 'Data Import', icon: Upload }
    ]
  },
  { 
    id: 'financial-statements', 
    path: '/financial-statements', 
    label: 'Financial Statements', 
    icon: DollarSign,
    children: [
      { id: 'balance-sheet', path: '/balance-sheet', label: 'Balance Sheet', icon: FileSpreadsheet },
      { id: 'income-statement', path: '/income-statement', label: 'Income Statement', icon: FileText }
    ]
  },
  { 
    id: 'reports', 
    path: '/reports', 
    label: 'Reports', 
    icon: BookOpen,
    children: [
      { id: 'mda', path: '/mda', label: 'MD&A', icon: FileText },
      { id: 'automated-reports', path: '/automated-reports', label: 'Automated Reports', icon: Calendar },
      { id: 'cross-entity-reporting', path: '/cross-entity-reporting', label: 'Cross-Entity Reports', icon: Building2 },
      { id: 'audit-workpapers', path: '/audit-workpapers', label: 'Audit Work Papers', icon: Shield }
    ]
  },
  { 
    id: 'analytics', 
    path: '/analytics', 
    label: 'Analytics', 
    icon: BarChart3,
    children: [
      { id: 'operational-dashboard', path: '/operational-dashboard', label: 'Operational Dashboard', icon: LayoutDashboard },
      { id: 'fpa-analysis', path: '/fpa-analysis', label: 'FP&A Analysis', icon: TrendingUp },
      { id: 'adhoc-analysis', path: '/adhoc-analysis', label: 'Ad-Hoc Analysis', icon: Database },
      { id: 'financial-analytics', path: '/financial-analytics', label: 'Financial Analytics', icon: Zap }
    ]
  },
  { 
    id: 'fpa', 
    path: '/fpa', 
    label: 'FP&A', 
    icon: TrendingUp,
    children: [
      { id: 'predictive-analytics', path: '/predictive-analytics', label: 'Predictive Analytics', icon: TrendingUp }
    ]
  },
  { 
    id: 'audit', 
    path: '/audit', 
    label: 'Audit', 
    icon: Shield,
    children: [
      { id: 'reconciliations', path: '/reconciliations', label: 'Reconciliations', icon: ClipboardCheck },
      { id: 'automated-reconciliation', path: '/automated-reconciliation', label: 'Automated Reconciliation', icon: Zap },
      { id: 'workpapers', path: '/workpapers', label: 'Work Papers', icon: FileText },
      { id: 'audit-trail', path: '/audit-trail', label: 'Audit Trail', icon: History }
    ]
  },
  { 
    id: 'data-management', 
    path: '/data-management', 
    label: 'Data Management', 
    icon: Database,
    children: [
      { id: 'import', path: '/import', label: 'Import Data', icon: Upload },
      { id: 'templates', path: '/templates', label: 'ERP Templates', icon: FileSpreadsheet },
      { id: 'cloud-storage', path: '/cloud-storage', label: 'Cloud Storage', icon: Cloud }
    ]
  }
];

const adminNavItems = [
  { 
    id: 'users', 
    path: '/users', 
    label: 'Users', 
    icon: Users,
    children: [
      { id: 'user-management', path: '/users', label: 'User Management', icon: Users, exact: true },
      { id: 'permissions', path: '/permissions', label: 'Permissions', icon: Shield },
      { id: 'mfa-enforcement', path: '/mfa-enforcement', label: 'MFA Enforcement', icon: KeyRound }
    ]
  }
];

function NavItem({ item, expandedItems, toggleExpanded }) {
  const Icon = item.icon;
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems[item.id];

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => toggleExpanded(item.id)}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group w-full text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm"
        >
          <Icon className="w-5 h-5 flex-shrink-0 transition-colors" />
          <span className="font-medium flex-1 text-left text-sm">{item.label}</span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 transition-transform duration-200" />
          ) : (
            <ChevronRight className="w-4 h-4 transition-transform duration-200" />
          )}
        </button>
        
        <div className={`ml-8 mt-1 space-y-0.5 overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          {item.children.map((child) => {
            const ChildIcon = child.icon;
            return (
              <NavLink
                key={child.id}
                to={child.path}
                end={child.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 group ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`
                }
              >
                <ChildIcon className="w-4 h-4" />
                <span className="text-sm font-medium">{child.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 group ${
          isActive
            ? 'bg-primary text-primary-foreground shadow-md'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm'
        }`
      }
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="font-medium text-sm">{item.label}</span>
    </NavLink>
  );
}

export default function EnhancedMainLayout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [entityLogo, setEntityLogo] = useState(null);
  const [entityName, setEntityName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({
    'accounts': true,
    'financial-statements': true,
    'reports': false,
    'analytics': false,
    'fpa': false,
    'audit': true,
    'data-management': false
  });

  // Initialize keyboard shortcuts
  useKeyboardShortcuts();

  // Listen for shortcuts help event
  useEffect(() => {
    const handleShowShortcuts = () => setShortcutsDialogOpen(true);
    window.addEventListener('show-shortcuts-help', handleShowShortcuts);
    return () => window.removeEventListener('show-shortcuts-help', handleShowShortcuts);
  }, []);

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  useEffect(() => {
    if (user) {
      fetchEntityInfo();
      loadProfilePhoto();
    }
  }, [user]);

  const fetchEntityInfo = async () => {
    try {
      const { data: entity } = await getEntity();
      setEntityName(entity.name);
      
      getEntityLogo()
        .then(({ data: logoBlob }) => {
          const logoUrl = URL.createObjectURL(logoBlob);
          setEntityLogo(logoUrl);
        })
        .catch(() => {
          setEntityLogo(null);
        });
    } catch (error) {
      console.error('Failed to fetch entity info:', error);
    }
  };

  const loadProfilePhoto = async () => {
    try {
      const { data } = await getProfilePhoto();
      if (data && data.size > 0) {
        const url = URL.createObjectURL(data);
        setProfilePhoto(url);
      }
    } catch (error) {
      setProfilePhoto(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const allNavItems = isAdmin ? [...defaultNavItems, ...adminNavItems] : defaultNavItems;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen z-50
        bg-card border-r border-border shadow-lg
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="p-4 border-b border-border bg-gradient-to-r from-primary/5 to-primary/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                {entityLogo ? (
                  <img src={entityLogo} alt="Logo" className="w-10 h-10 rounded-lg object-cover shadow-sm" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center shadow-sm">
                    <Building2 className="w-6 h-6 text-primary-foreground" />
                  </div>
                )}
                {sidebarOpen && (
                  <div className="flex-1 min-w-0">
                    <h1 className="text-sm font-bold truncate bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                      {entityName || 'Obsidian Ledger'}
                    </h1>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:flex h-8 w-8 hover:bg-primary/10"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Menu className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden h-8 w-8"
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Entity Switcher */}
          {sidebarOpen && (
            <div className="px-3 py-2 border-b border-border bg-muted/30">
              <EntitySwitcher />
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {allNavItems.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  expandedItems={expandedItems}
                  toggleExpanded={toggleExpanded}
                />
              ))}
            </nav>
          </ScrollArea>

          {/* Bottom Section */}
          <div className="p-3 border-t border-border space-y-2 bg-muted/20">
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`
              }
            >
              <Settings className="w-5 h-5" />
              {sidebarOpen && <span className="text-sm font-medium">Settings</span>}
            </NavLink>
            <button
              onClick={() => setShortcutsDialogOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <HelpCircle className="w-5 h-5" />
              {sidebarOpen && <span className="text-sm font-medium">Shortcuts</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            <div className="flex items-center gap-4 flex-1">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>

              {/* Breadcrumbs */}
              <Breadcrumbs />
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <form onSubmit={handleSearch} className="hidden md:flex items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search... (Ctrl+K)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent w-48 lg:w-64 transition-all"
                  />
                </div>
              </form>

              {/* Notifications */}
              <NotificationDropdown />

              {/* User Menu */}
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
                <div className="hidden md:block text-right">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.role}</p>
                </div>
                <div className="relative group">
                  <button className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-primary-foreground font-semibold shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105">
                    {profilePhoto ? (
                      <img src={profilePhoto} alt="Profile" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                  </button>
                  
                  {/* Dropdown */}
                  <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="p-2 space-y-1">
                      <button
                        onClick={() => navigate('/settings')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent text-sm transition-colors"
                      >
                        <User className="w-4 h-4" />
                        <span>Profile</span>
                      </button>
                      <button
                        onClick={() => navigate('/settings')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent text-sm transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </button>
                      <Separator className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-destructive/10 text-destructive text-sm transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-gradient-to-br from-background to-muted/20">
          <Outlet />
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette />

      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog 
        open={shortcutsDialogOpen} 
        onClose={() => setShortcutsDialogOpen(false)} 
      />
    </div>
  );
}